import React from 'react';
import { useTranslation } from 'react-i18next';
import { Settings2Icon, VolumeIcon, BrainIcon, SaveIcon } from 'lucide-react';
import { useInterviewStore } from '../store';
import toast from 'react-hot-toast';

export function Settings() {
  const { t } = useTranslation();
  const config = useInterviewStore((state) => state.interviewConfig);
  const updateConfig = useInterviewStore((state) => state.updateConfig);

  const handleSaveSettings = () => {
    updateConfig(config);
    toast.success(t('settings.saved'));
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 space-y-8">
      <div className="flex items-center gap-3 border-b pb-4">
        <Settings2Icon className="h-6 w-6 text-indigo-600" />
        <h2 className="text-2xl font-bold text-gray-900">
          {t('settings.title')}
        </h2>
      </div>

      {/* AI Model Settings */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <BrainIcon className="h-5 w-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            {t('settings.ai.title')}
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.ai.model')}
            </label>
            <select
              value={config.model}
              onChange={(e) => updateConfig({ model: e.target.value })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Default)</option>
              <option value="gpt-4">GPT-4 (Premium)</option>
            </select>
            <p className="mt-1 text-sm text-gray-500">
              {config.model === 'gpt-3.5-turbo' 
                ? 'Standard model - Good balance of performance and cost'
                : 'Premium model - Higher accuracy but more expensive'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.ai.temperature')}
            </label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={config.temperature}
              onChange={(e) => updateConfig({ temperature: parseFloat(e.target.value) })}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-gray-500">
              <span>0.0</span>
              <span>{config.temperature}</span>
              <span>2.0</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.ai.maxTokens')}
            </label>
            <input
              type="number"
              min="100"
              max="4000"
              step="100"
              value={config.maxTokens}
              onChange={(e) => updateConfig({ maxTokens: parseInt(e.target.value) })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>
        </div>
      </div>

      {/* Voice Settings */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <VolumeIcon className="h-5 w-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            {t('settings.voice.title')}
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.voice.pitch')}
            </label>
            <input
              type="range"
              min="0.5"
              max="2"
              step="0.1"
              value={config.voice?.pitch}
              onChange={(e) => updateConfig({ voice: { ...config.voice, pitch: parseFloat(e.target.value) } })}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-gray-500">
              <span>0.5</span>
              <span>{config.voice?.pitch}</span>
              <span>2.0</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.voice.rate')}
            </label>
            <input
              type="range"
              min="0.5"
              max="2"
              step="0.1"
              value={config.voice?.rate}
              onChange={(e) => updateConfig({ voice: { ...config.voice, rate: parseFloat(e.target.value) } })}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-gray-500">
              <span>0.5</span>
              <span>{config.voice?.rate}</span>
              <span>2.0</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSaveSettings}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
        >
          <SaveIcon className="h-5 w-5" />
          {t('settings.save')}
        </button>
      </div>
    </div>
  );
}